import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRightIcon, MonitorIcon, SmartphoneIcon, TabletIcon } from 'lucide-react'
import { useDateWindow } from '@/components/date-range-picker'
import { DeltaInline } from '@/components/delta'
import { Flag } from '@/components/flag'
import { Panel } from '@/components/panel'
import { PerformancePanel } from '@/components/performance-panel'
import { RankedList } from '@/components/ranked-list'
import { countryName } from '@/lib/countries'
import { formatCount, relativeDelta, shortenUrl } from '@/lib/format'
import type { DateRange } from '@/lib/queries'
import type { MetricsRow } from '@/lib/site-data'
import {
  DEFAULT_TABLE,
  useGeo,
  useOverview,
  usePagesBreakdown,
  useQueriesBreakdown,
} from '@/lib/site-data'
import { cn } from '@/lib/utils'
import { MetricsSkeleton, SectionHeader, SiteSectionBody, useSiteScope } from '../../-section'

export const Route = createFileRoute('/_app/$org/$host/search/_list/')({
  component: SiteSearchPage,
})

const PREVIEW = { ...DEFAULT_TABLE, limit: 5 }

function SiteSearchPage() {
  const { org, host, range, site } = useSiteScope()
  const { data, isPending } = useOverview(site, range)
  const geo = useGeo(site, range)
  const keywords = useQueriesBreakdown(site, range, PREVIEW, 'all')
  const pages = usePagesBreakdown(site, range, PREVIEW, 'all')
  useDateWindow(data?.range)

  return (
    <>
      <SectionHeader title="Search" />

      <SiteSectionBody
        org={org}
        host={host}
        range={range}
        result={data}
        isPending={isPending}
        rowCount={data?.series.length ?? 0}
        skeleton={<MetricsSkeleton />}
      >
        <div className="space-y-4">
          <PerformancePanel
            totals={data?.totals}
            series={data?.series ?? []}
            previousSeries={data?.previousSeries}
            bing={data?.bing}
            rangeStart={data?.range.from}
            previousStart={data?.previousRange?.from}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel
              title="Top keywords"
              flush
              actions={<ViewAll to="/$org/$host/keywords" params={{ org, host }} range={range} />}
            >
              <RankedList
                dimension="Keyword"
                rows={
                  keywords.data?.rows.map((row) => ({
                    key: String(row.queryId),
                    label: row.query,
                    mono: true,
                    metrics: row,
                  })) ?? []
                }
                empty="No keywords in this window yet."
                renderLabel={(row) => (
                  <Link
                    to="/$org/$host/keywords/$queryId"
                    params={{ org, host, queryId: row.key }}
                    search={{ range }}
                    className="underline-offset-2 hover:text-plum hover:underline"
                  >
                    {row.label}
                  </Link>
                )}
              />
            </Panel>

            <Panel
              title="Top pages"
              flush
              actions={<ViewAll to="/$org/$host/pages" params={{ org, host }} range={range} />}
            >
              <RankedList
                dimension="Page"
                rows={
                  pages.data?.rows.map((row) => ({
                    key: String(row.pageId),
                    label: shortenUrl(row.url, host),
                    mono: true,
                    metrics: row,
                  })) ?? []
                }
                empty="No pages in this window yet."
                renderLabel={(row) => (
                  <Link
                    to="/$org/$host/pages/$pageId"
                    params={{ org, host, pageId: row.key }}
                    search={{ range }}
                    className="underline-offset-2 hover:text-plum hover:underline"
                  >
                    {row.label}
                  </Link>
                )}
              />
            </Panel>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel
              title="Countries"
              flush
              meta={geo.data?.countries.total ? `${geo.data.countries.total}` : undefined}
            >
              <RankedList
                dimension="Country"
                rows={
                  geo.data?.countries.rows.map((row) => ({
                    key: row.country,
                    label: countryName(row.country),
                    leading: <Flag code={row.country} />,
                    metrics: row,
                  })) ?? []
                }
                empty="No countries in this window yet."
              />
            </Panel>

            <Panel title="Devices" flush>
              <Devices rows={geo.data?.devices ?? []} />
            </Panel>
          </div>
        </div>
      </SiteSectionBody>
    </>
  )
}

function ViewAll({
  to,
  params,
  range,
}: {
  to: '/$org/$host/keywords' | '/$org/$host/pages'
  params: { org: string; host: string }
  range: DateRange
}) {
  return (
    <Link
      to={to}
      params={params}
      search={{ range }}
      className="mono-label flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
    >
      All
      <ArrowRightIcon className="size-3" aria-hidden />
    </Link>
  )
}

const DEVICE_ICONS = {
  desktop: MonitorIcon,
  mobile: SmartphoneIcon,
  tablet: TabletIcon,
} as const

const DEVICE_LABELS = {
  desktop: 'Desktop',
  mobile: 'Mobile',
  tablet: 'Tablet',
} as const

/**
 * Three values, so the split itself is the story: one bar for the whole, then
 * the rows underneath as its key. Opacity separates the segments rather than
 * three colours — the accent should still mean one thing.
 */
function Devices({ rows }: { rows: (MetricsRow & { device: 'desktop' | 'mobile' | 'tablet' })[] }) {
  if (rows.length === 0) {
    return (
      <p className="px-4 py-3 text-[13px] text-muted-foreground">No devices in this window yet.</p>
    )
  }

  const total = rows.reduce((sum, row) => sum + row.clicks, 0) || 1
  const shades = ['bg-plum', 'bg-plum/60', 'bg-plum/30'] as const

  return (
    <div>
      <div className="flex h-1.5 gap-px overflow-hidden rounded-full bg-secondary mx-4 mt-3">
        {rows.map((row, index) => (
          <span
            key={row.device}
            className={shades[index] ?? 'bg-plum/20'}
            style={{ width: `${(row.clicks / total) * 100}%` }}
            aria-hidden
          />
        ))}
      </div>

      <ul className="px-4 py-2">
        {rows.map((row, index) => {
          const Icon = DEVICE_ICONS[row.device]
          return (
            <li key={row.device} className="flex items-center gap-2.5 py-[5px] text-[12.5px]">
              <span
                className={cn('size-1.5 shrink-0 rounded-full', shades[index] ?? 'bg-plum/20')}
                aria-hidden
              />
              <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{DEVICE_LABELS[row.device]}</span>
              <span className="w-12 shrink-0 text-right font-mono tabular">
                {formatCount(row.clicks)}
              </span>
              <span className="w-10 shrink-0 text-right font-mono text-[11px] text-muted-foreground tabular">
                {Math.round((row.clicks / total) * 100)}%
              </span>
              <span className="w-10 shrink-0 text-right">
                <DeltaInline
                  value={relativeDelta(row.clicks, row.previous?.clicks ?? null)}
                  className="justify-end"
                />
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
